import React from "react";
import {
  ArrowRight,
  Bell,
  BellRing,
  CalendarClock,
  Check,
  ChevronRight,
  Cloud,
  Coffee,
  Croissant,
  Egg,
  FileText,
  Ghost,
  Gift,
  LineChart,
  Milk,
  MoreHorizontal,
  Music,
  RotateCcw,
  ScanLine,
  ShoppingBasket,
  Sparkles,
  Sun,
  TicketPercent,
  Timer,
  TrendingUp,
  Tv,
  Trophy,
  Zap,
  ShieldAlert,
  ShieldCheck,
  Info,
  Plane,
} from "lucide-react";

// -------------------------------------------------------------------------
// Shared shells
// -------------------------------------------------------------------------
export const SectionShell = ({ id, eyebrow, title, description, children, testId, tight = false }) => (
  <section
    id={id}
    className={tight ? "relative py-12 md:py-16" : "relative py-14 md:py-20"}
    data-testid={testId}
  >
    <div className="mx-auto max-w-[1200px] px-4 md:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
          {eyebrow}
        </div>
        <h2
          className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          {title}
        </h2>
        {description ? (
          <p className="mt-4 text-[15px] md:text-base text-[#C7D0DF] leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>
      <div className="mt-10 md:mt-12">{children}</div>
    </div>
  </section>
);

export const GlassCard = ({ children, className = "", style = {}, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-3xl relative overflow-hidden ${className}`}
    style={{
      background:
        "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow:
        "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      ...style,
    }}
  >
    {children}
  </div>
);

// =========================================================================
// SECTION 2 — SAFE TO SPEND (big balance graphic + greeting card)
// =========================================================================
export const SafeToSpendSection = () => (
  <SectionShell
    id="safe-to-spend"
    eyebrow="Safe to Spend"
    title="Your real balance, minus reality."
    description="Bills, subscriptions, upcoming plans — one number so you know exactly what's safe to spend today, tomorrow, and until payday."
    testId="section-safe-to-spend"
  >
    <div className="max-w-2xl mx-auto flex flex-col gap-5">
      {/* BIG SAFE-TO-SPEND GRAPHIC */}
      <GlassCard
        testId="safe-to-spend-graphic"
        className="p-8 md:p-10"
        style={{
          background:
            "linear-gradient(160deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.04) 45%, rgba(255,255,255,0.03) 100%)",
          border: "1px solid rgba(34,197,133,0.35)",
          boxShadow:
            "0 50px 100px -40px rgba(34,197,133,0.35), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30 blur-3xl"
          style={{ background: "#22C585" }}
        />
        <div className="relative flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{
              background: "rgba(34,197,133,0.18)",
              border: "1px solid rgba(34,197,133,0.50)",
              color: "#22C585",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "#22C585", boxShadow: "0 0 10px rgba(34,197,133,0.9)" }}
            />
            Safe to Spend · On Track
          </div>
          <div className="text-[10px] uppercase tracking-[0.20em] text-white/60 font-semibold">
            Cycle · 62% elapsed
          </div>
        </div>
        <div className="relative mt-5 flex items-baseline gap-2">
          <span
            className="text-6xl md:text-7xl font-extrabold text-white leading-none tracking-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            $712
          </span>
          <span
            className="text-2xl md:text-3xl font-bold text-white/60"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            .00
          </span>
        </div>
        <div className="relative mt-2 text-[13px] md:text-sm text-[#C7D0DF]">
          Available to spend through payday · 6 days left
        </div>
        <div
          className="relative mt-6 w-full h-2 rounded-full overflow-hidden"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: "62%",
              background: "linear-gradient(90deg, #22C585 0%, #7AF0BA 100%)",
              boxShadow: "0 0 20px rgba(34,197,133,0.55)",
            }}
          />
        </div>
        <div className="relative mt-4 grid grid-cols-3 gap-3">
          {[
            { k: "Bills", v: "$284" },
            { k: "Subs", v: "$47" },
            { k: "Buffer", v: "$120" },
          ].map((r) => (
            <div
              key={r.k}
              className="rounded-xl px-3 py-2.5"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <div className="text-[9px] uppercase tracking-[0.20em] text-white/50 font-bold">
                {r.k}
              </div>
              <div
                className="mt-0.5 text-sm font-extrabold text-white"
                style={{ fontFamily: "Sora, Inter, sans-serif" }}
              >
                {r.v}
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* GREETING CARD */}
      <GlassCard testId="safe-to-spend-greeting" className="p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background:
                "linear-gradient(140deg, rgba(244,199,82,0.28) 0%, rgba(244,199,82,0.08) 100%)",
              border: "1px solid rgba(244,199,82,0.45)",
              boxShadow: "0 20px 40px -20px rgba(244,199,82,0.40)",
            }}
          >
            <Sun size={22} className="text-[#F4C752]" />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-lg md:text-xl font-extrabold text-white leading-tight"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              Good morning, Topaz.
            </div>
            <p className="mt-1 text-[14px] md:text-[15px] text-[#C7D0DF] leading-relaxed">
              You have{" "}
              <span className="text-[#22C585] font-bold">$712 safe to spend</span>{" "}
              until payday, which is in{" "}
              <span className="text-white font-bold">6 days</span>.
            </p>
          </div>
        </div>
      </GlassCard>
    </div>
  </SectionShell>
);

// =========================================================================
// SECTION 3 — SMART SHOPPING CART (ghost items + real items + total)
// =========================================================================
export const SmartCartSection = () => (
  <SectionShell
    id="smart-cart"
    eyebrow="Smart Cart"
    title="Every basket, pre-priced."
    description="Scan your basket at the door. Real items land in your cart. Ghost items — the ones that could blow your budget — are held for review before checkout."
    testId="section-smart-cart"
  >
    <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr] items-start">
      {/* LEFT — Cart mockup */}
      <GlassCard testId="smart-cart-graphic" className="p-6 md:p-7">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-15 blur-3xl"
          style={{ background: "#22C585" }}
        />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: "rgba(34,197,133,0.18)",
                border: "1px solid rgba(34,197,133,0.45)",
              }}
            >
              <ShoppingBasket size={18} className="text-[#22C585]" />
            </div>
            <div>
              <div
                className="text-[15px] font-extrabold text-white leading-tight"
                style={{ fontFamily: "Sora, Inter, sans-serif" }}
              >
                Smart Basket
              </div>
              <div className="text-[11px] text-white/60">4 items · 3 ghost held</div>
            </div>
          </div>
          <div
            className="text-[10px] font-bold uppercase tracking-[0.20em] px-2 py-1 rounded-md"
            style={{
              background: "rgba(34,197,133,0.14)",
              border: "1px solid rgba(34,197,133,0.40)",
              color: "#22C585",
            }}
          >
            Live scan
          </div>
        </div>

        {/* Real items */}
        <div className="relative mt-5">
          <div className="text-[10px] uppercase tracking-[0.20em] text-white/50 font-bold">
            In basket
          </div>
          <div className="mt-2 space-y-2">
            {[
              { icon: Egg, k: "Eggs · dozen", v: "$4.99" },
              { icon: Croissant, k: "Sourdough bread", v: "$3.49" },
              { icon: Milk, k: "Whole milk · 1 gal", v: "$4.29" },
              { icon: Coffee, k: "Cold brew · 32oz", v: "$5.99" },
            ].map((r) => (
              <div
                key={r.k}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(34,197,133,0.15)",
                    border: "1px solid rgba(34,197,133,0.35)",
                  }}
                >
                  <r.icon size={14} className="text-[#22C585]" />
                </div>
                <div className="flex-1 text-[13px] text-white font-semibold">{r.k}</div>
                <div className="text-[13px] text-white font-bold">{r.v}</div>
                <Check size={14} className="text-[#22C585]" strokeWidth={3} />
              </div>
            ))}
          </div>
        </div>

        {/* Ghost items */}
        <div className="relative mt-5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.20em] text-[#B7A5FF] font-bold">
            <Ghost size={11} strokeWidth={2.5} />
            Ghost items · held for review
          </div>
          <div className="mt-2 space-y-2">
            {[
              { k: "Ice cream · pint", v: "~$5.99" },
              { k: "Chips · family bag", v: "~$4.29" },
              { k: "Wine · 750ml", v: "~$14.99" },
            ].map((r) => (
              <div
                key={r.k}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{
                  background: "rgba(183,165,255,0.06)",
                  border: "1px dashed rgba(183,165,255,0.40)",
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(183,165,255,0.14)",
                    border: "1px solid rgba(183,165,255,0.35)",
                  }}
                >
                  <Ghost size={14} className="text-[#B7A5FF]" />
                </div>
                <div className="flex-1 text-[13px] text-white/80 font-medium line-through decoration-white/30">
                  {r.k}
                </div>
                <div className="text-[13px] text-[#B7A5FF] font-bold">{r.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Total row */}
        <div
          className="relative mt-5 rounded-2xl p-4 flex items-center justify-between"
          style={{
            background:
              "linear-gradient(120deg, rgba(34,197,133,0.14) 0%, rgba(34,197,133,0.04) 100%)",
            border: "1px solid rgba(34,197,133,0.40)",
          }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.20em] text-[#22C585] font-bold">
              Total · in basket
            </div>
            <div
              className="mt-0.5 text-2xl font-extrabold text-white"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              $18.76
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.20em] text-white/60 font-bold">
              Impact on Safe to Spend
            </div>
            <div
              className="mt-0.5 text-lg font-extrabold text-[#22C585]"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              − 2.6%
            </div>
          </div>
        </div>
      </GlassCard>

      {/* RIGHT — Callouts */}
      <div className="flex flex-col gap-4">
        {[
          {
            icon: ShoppingBasket,
            color: "#22C585",
            title: "Real items — in basket.",
            body: "Every scanned item lands in your Smart Basket with live pricing so your Safe to Spend updates in real time.",
          },
          {
            icon: Ghost,
            color: "#B7A5FF",
            title: "Ghost items — held for review.",
            body: "Anything that would blow the budget is quietly held as a ghost. Approve it or leave it — nothing surprises you at checkout.",
          },
          {
            icon: Sparkles,
            color: "#22C585",
            title: "Checkout without the guesswork.",
            body: "Walk to the register knowing the total, the impact, and whether it's actually safe to spend today.",
          },
        ].map((r) => (
          <GlassCard key={r.title} className="p-5">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background:
                    r.color === "#22C585"
                      ? "rgba(34,197,133,0.15)"
                      : "rgba(183,165,255,0.15)",
                  border: `1px solid ${r.color === "#22C585" ? "rgba(34,197,133,0.40)" : "rgba(183,165,255,0.40)"}`,
                }}
              >
                <r.icon size={17} style={{ color: r.color }} />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[15px] font-extrabold text-white leading-tight"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                >
                  {r.title}
                </div>
                <p className="mt-1 text-[13.5px] text-[#C7D0DF] leading-relaxed">{r.body}</p>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  </SectionShell>
);

// =========================================================================
// SECTION 4 — SUBSCRIPTIONS TRACKER (with 2-day auto-renew alert)
// =========================================================================
export const SubscriptionsSection = () => (
  <SectionShell
    id="subscriptions"
    eyebrow="Subscriptions Manager"
    title="Never surprised by an auto-renew."
    description="Every subscription pinged in the Nerve Center. Two days before it charges, we alert you so you can keep it, pause it, or cancel it — without the surprise hit."
    testId="section-subscriptions"
  >
    <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr] items-start">
      {/* LEFT — Alert callout */}
      <div className="flex flex-col gap-4">
        <GlassCard
          className="p-5 md:p-6 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(160deg, rgba(244,199,82,0.18) 0%, rgba(244,199,82,0.04) 100%)",
            border: "1px solid rgba(244,199,82,0.45)",
            boxShadow:
              "0 40px 80px -40px rgba(244,199,82,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
          testId="subscription-alert-card"
        >
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-25 blur-3xl"
            style={{ background: "#F4C752" }}
          />
          <div className="relative flex items-center gap-2">
            <BellRing size={16} className="text-[#F4C752]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F4C752] font-bold">
              Renewal alert
            </div>
          </div>
          <div
            className="relative mt-2 text-2xl md:text-[26px] font-extrabold text-white leading-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            You have 2 days before this auto-renews.
          </div>
          <p className="relative mt-2 text-[13.5px] text-[#E5EAF3] leading-relaxed">
            Cancel, pause, or keep it. Whatever you choose, SpendNova will
            reflect the change in your Safe to Spend before payday.
          </p>
          <div className="relative mt-4 flex flex-wrap gap-2">
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold"
              style={{ background: "#22C585", color: "#04111F" }}
            >
              <Check size={13} strokeWidth={3} />
              Keep it
            </div>
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.20)",
              }}
            >
              Pause 1 cycle
            </div>
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white"
              style={{
                background: "rgba(239,107,107,0.14)",
                border: "1px solid rgba(239,107,107,0.45)",
                color: "#EF6B6B",
              }}
            >
              Cancel
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(34,197,133,0.15)",
                border: "1px solid rgba(34,197,133,0.40)",
              }}
            >
              <CalendarClock size={17} className="text-[#22C585]" />
            </div>
            <div className="min-w-0">
              <div
                className="text-[15px] font-extrabold text-white leading-tight"
                style={{ fontFamily: "Sora, Inter, sans-serif" }}
              >
                Two-day heads-up. Every time.
              </div>
              <p className="mt-1 text-[13.5px] text-[#C7D0DF] leading-relaxed">
                We ping you 48 hours before any subscription auto-renews —
                streaming, gym, cloud, gaming, everything.
              </p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* RIGHT — Subscription list */}
      <GlassCard className="p-5 md:p-6" testId="subscription-list-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
              Active subscriptions
            </div>
            <div
              className="mt-0.5 text-lg font-extrabold text-white"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              $47.96 / month
            </div>
          </div>
          <MoreHorizontal size={18} className="text-white/50" />
        </div>
        <div className="mt-4 space-y-2">
          {[
            { icon: Tv, k: "Netflix Premium", d: "Renews in 2 days", v: "$15.99", alert: true },
            { icon: Music, k: "Spotify Family", d: "Renews Nov 14", v: "$16.99", alert: false },
            { icon: Zap, k: "ChatGPT Plus", d: "Renews Nov 21", v: "$20.00", alert: false },
            { icon: Cloud, k: "iCloud+ 200GB", d: "Renews Nov 28", v: "$2.99", alert: false },
          ].map((r) => (
            <div
              key={r.k}
              className="flex items-center gap-3 px-3 py-3 rounded-xl"
              style={
                r.alert
                  ? {
                      background:
                        "linear-gradient(120deg, rgba(244,199,82,0.14) 0%, rgba(244,199,82,0.03) 100%)",
                      border: "1px solid rgba(244,199,82,0.45)",
                    }
                  : {
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }
              }
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: r.alert
                    ? "rgba(244,199,82,0.16)"
                    : "rgba(255,255,255,0.06)",
                  border: r.alert
                    ? "1px solid rgba(244,199,82,0.45)"
                    : "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <r.icon
                  size={16}
                  style={{ color: r.alert ? "#F4C752" : "#C7D0DF" }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] text-white font-semibold truncate">{r.k}</div>
                <div
                  className="text-[11px] truncate"
                  style={{ color: r.alert ? "#F4C752" : "#A8B0BE" }}
                >
                  {r.alert ? (
                    <span className="inline-flex items-center gap-1 font-bold">
                      <Bell size={10} strokeWidth={3} />
                      {r.d}
                    </span>
                  ) : (
                    r.d
                  )}
                </div>
              </div>
              <div className="text-[13px] font-bold text-white shrink-0">{r.v}</div>
              <ChevronRight size={14} className="text-white/40 shrink-0" />
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  </SectionShell>
);

// =========================================================================
// SECTION 5 — VOUCHER VAULT (proprietary specimen worksheet)
// =========================================================================
export const VoucherVaultSection = () => {
  const rows = [
    { k: "Traveler", v: "PFC A. Ramirez" },
    { k: "Home station", v: "Fort Bragg, NC" },
    { k: "TDY location", v: "Fort Lee, VA" },
    { k: "Duration", v: "14 days" },
  ];
  const calc = [
    { k: "Lodging", d: "$124 / day × 14", v: "$1,736.00" },
    { k: "M&IE per-diem", d: "$59 / day × 14", v: "$826.00" },
    { k: "Incidentals", d: "$5 / day × 14", v: "$70.00" },
    { k: "Mileage", d: "312 mi × $0.67", v: "$209.04" },
  ];
  return (
    <SectionShell
      id="voucher-vault"
      eyebrow="Voucher Vault"
      title="A specimen worksheet, not a form."
      description="An educational sample that helps you format and calculate your travel voucher before you file. Proprietary framework — pairs with the receipts scanned into your Voucher Vault."
      testId="section-voucher-vault"
    >
      <div className="max-w-3xl mx-auto">
        <GlassCard className="p-6 md:p-8" testId="voucher-worksheet-card">
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-15 blur-3xl"
            style={{ background: "#22C585" }}
          />
          {/* Worksheet header */}
          <div className="relative flex items-center justify-between pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{
                  background: "rgba(34,197,133,0.15)",
                  border: "1px solid rgba(34,197,133,0.40)",
                }}
              >
                <FileText size={18} className="text-[#22C585]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
                  SpendNova · Specimen
                </div>
                <div
                  className="text-lg md:text-xl font-extrabold text-white leading-tight"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                >
                  Voucher Preparation Worksheet
                </div>
              </div>
            </div>
            <div
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest"
              style={{
                background: "rgba(183,165,255,0.14)",
                border: "1px solid rgba(183,165,255,0.40)",
                color: "#B7A5FF",
              }}
            >
              <Info size={11} />
              Sample template
            </div>
          </div>

          {/* Two-column details */}
          <div className="relative mt-5 grid gap-2.5 sm:grid-cols-2">
            {rows.map((r) => (
              <div
                key={r.k}
                className="flex items-center justify-between px-3.5 py-2.5 rounded-lg"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <span className="text-[11px] uppercase tracking-[0.16em] text-white/60 font-bold">
                  {r.k}
                </span>
                <span className="text-[13px] text-white font-semibold">{r.v}</span>
              </div>
            ))}
          </div>

          {/* Calc rows */}
          <div className="relative mt-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold px-1">
              Line-item calculations
            </div>
            <div className="mt-2 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
              {calc.map((r, i) => (
                <div
                  key={r.k}
                  className="flex items-center justify-between px-4 py-3"
                  style={{
                    background:
                      i % 2 === 0 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
                    borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] text-white font-semibold">{r.k}</div>
                    <div className="text-[11px] text-white/55">{r.d}</div>
                  </div>
                  <div
                    className="text-[13px] text-white font-bold"
                    style={{ fontFamily: "Sora, Inter, sans-serif" }}
                  >
                    {r.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div
            className="relative mt-5 rounded-2xl p-4 md:p-5 grid gap-3 sm:grid-cols-3"
            style={{
              background:
                "linear-gradient(120deg, rgba(34,197,133,0.14) 0%, rgba(34,197,133,0.04) 100%)",
              border: "1px solid rgba(34,197,133,0.40)",
            }}
          >
            {[
              { k: "Total claimed", v: "$2,841.04", tone: "#FFFFFF" },
              { k: "Amount received", v: "$2,400.00", tone: "#C7D0DF" },
              { k: "Balance due traveler", v: "$441.04", tone: "#22C585" },
            ].map((r) => (
              <div key={r.k}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">
                  {r.k}
                </div>
                <div
                  className="mt-1 text-xl md:text-[22px] font-extrabold"
                  style={{ fontFamily: "Sora, Inter, sans-serif", color: r.tone }}
                >
                  {r.v}
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div
            className="relative mt-5 rounded-xl p-3 flex items-start gap-2.5"
            style={{
              background: "rgba(183,165,255,0.06)",
              border: "1px dashed rgba(183,165,255,0.35)",
            }}
          >
            <ShieldAlert size={15} className="text-[#B7A5FF] shrink-0 mt-0.5" />
            <p className="text-[12px] text-[#C7D0DF] leading-relaxed">
              <span className="text-white font-semibold">
                Sample template — not an official DoD form.
              </span>{" "}
              For educational reference only. SpendNova™ is a privately owned
              commercial utility and is NOT affiliated with, endorsed by, or
              a product of the Department of Defense (DoD) or any United
              States military branch.
            </p>
          </div>
        </GlassCard>
      </div>
    </SectionShell>
  );
};

// =========================================================================
// SECTION 5.5 — TRAVEL BUDGETING & TRIP PLANNING
// =========================================================================
export const TravelBudgetingSection = () => {
  const features = [
    "Vacation Budget Planning",
    "Cruise Budget Planning",
    "PCS & Military Travel Cost Planning",
    "TDY Travel Budget Estimates",
    "Business Trip Planning",
    "Flight Price Comparison",
    "Hotel Cost Comparison",
    "Rental Car Estimates",
    "Live Travel Pricing (via trusted travel partners)",
    "Safe to Spend™ Travel Affordability Analysis",
  ];

  return (
    <SectionShell
      id="travel-budgeting"
      eyebrow="Travel Budgeting & Trip Planning"
      title="Know what's safe to spend before you book."
      testId="section-travel-budgeting"
    >
      <div className="max-w-3xl mx-auto">
        <GlassCard className="p-6 md:p-8" testId="travel-budgeting-card">
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-15 blur-3xl"
            style={{ background: "#22C585" }}
          />

          {/* Header */}
          <div className="relative flex items-center justify-between pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{
                  background: "rgba(34,197,133,0.15)",
                  border: "1px solid rgba(34,197,133,0.40)",
                }}
              >
                <Plane size={18} className="text-[#22C585]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
                  SpendNova · Travel Planner
                </div>
                <div
                  className="text-lg md:text-xl font-extrabold text-white leading-tight"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                >
                  Plan the trip. Protect the paycheck.
                </div>
              </div>
            </div>
            <div
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest"
              style={{
                background: "rgba(183,165,255,0.14)",
                border: "1px solid rgba(183,165,255,0.40)",
                color: "#B7A5FF",
              }}
            >
              <Info size={11} />
              Budgeting utility
            </div>
          </div>

          {/* Body copy */}
          <div className="relative mt-5 space-y-3.5 text-[14px] md:text-[15px] text-[#C7D0DF] leading-relaxed">
            <p data-testid="travel-body-1">
              SpendNova helps users determine whether they can comfortably
              afford a vacation, cruise, business trip, TDY, or PCS move
              before they book.
            </p>
            <p data-testid="travel-body-2">
              By combining intelligent budgeting tools with live travel
              pricing data from trusted travel providers, SpendNova estimates
              trip costs and compares them against each user's{" "}
              <span className="text-white font-semibold">Safe to Spend™</span>{" "}
              amount, helping users make informed financial decisions before
              committing to travel expenses.
            </p>
            <p data-testid="travel-body-3">
              Users can explore flights, hotels, cruises, rental cars, and
              other travel-related expenses while keeping their financial
              goals in focus.
            </p>
          </div>

          {/* Key features */}
          <div className="relative mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold px-1">
              Key Features
            </div>
            <div className="mt-2 rounded-xl overflow-hidden grid sm:grid-cols-2" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
              {features.map((f, i) => (
                <div
                  key={f}
                  className="flex items-start gap-2.5 px-4 py-3"
                  style={{
                    background:
                      i % 2 === 0 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
                    borderTop:
                      i < 2 ? "none" : "1px solid rgba(255,255,255,0.06)",
                  }}
                  data-testid={`travel-feature-${i}`}
                >
                  <Check
                    size={14}
                    className="text-[#22C585] shrink-0 mt-0.5"
                  />
                  <div className="text-[13px] text-white font-semibold leading-snug">
                    {f}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div
            className="relative mt-5 rounded-xl p-3 flex items-start gap-2.5"
            style={{
              background: "rgba(183,165,255,0.06)",
              border: "1px dashed rgba(183,165,255,0.35)",
            }}
            data-testid="travel-disclaimer"
          >
            <ShieldAlert size={15} className="text-[#B7A5FF] shrink-0 mt-0.5" />
            <p className="text-[12px] text-[#C7D0DF] leading-relaxed">
              <span className="text-white font-semibold">Disclaimer:</span>{" "}
              SpendNova provides budgeting tools and travel cost estimates
              using trusted third-party travel data providers. SpendNova is
              not a travel agency and does not provide travel advisory
              services.
            </p>
          </div>
        </GlassCard>
      </div>
    </SectionShell>
  );
};

// =========================================================================
// SECTION 6 — NOVA LIVE FEED: PROACTIVE AI BRAIN
//   • Marketing showcase for the "AI brain" that powers the Live Feed.
//   • 3 signature defenses: Return Window Defense · Coupon/Reward Guard ·
//     Predictive Basket forecasting.
//   • Live examples below (holiday nudge + savings win).
// =========================================================================
const DefenseCard = ({
  tone,
  icon: Icon,
  label,
  title,
  body,
  alert,
  testId,
}) => {
  const palette = {
    green: {
      accent: "#22C585",
      bg: "linear-gradient(160deg, rgba(34,197,133,0.16) 0%, rgba(34,197,133,0.04) 100%)",
      border: "rgba(34,197,133,0.42)",
      chip: "rgba(34,197,133,0.16)",
      chipBorder: "rgba(34,197,133,0.45)",
    },
    amber: {
      accent: "#F4C752",
      bg: "linear-gradient(160deg, rgba(244,199,82,0.14) 0%, rgba(244,199,82,0.03) 100%)",
      border: "rgba(244,199,82,0.42)",
      chip: "rgba(244,199,82,0.16)",
      chipBorder: "rgba(244,199,82,0.45)",
    },
    purple: {
      accent: "#B7A5FF",
      bg: "linear-gradient(160deg, rgba(183,165,255,0.14) 0%, rgba(183,165,255,0.03) 100%)",
      border: "rgba(183,165,255,0.42)",
      chip: "rgba(183,165,255,0.16)",
      chipBorder: "rgba(183,165,255,0.45)",
    },
  }[tone];

  return (
    <div
      className="rounded-3xl p-6 md:p-7 relative overflow-hidden h-full flex flex-col"
      data-testid={testId}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow:
          "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-20 blur-3xl"
        style={{ background: palette.accent }}
      />
      <div className="relative flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: palette.chip,
            border: `1px solid ${palette.chipBorder}`,
            boxShadow: `0 20px 40px -20px ${palette.accent}55`,
          }}
        >
          <Icon size={19} style={{ color: palette.accent }} />
        </div>
        <div
          className="text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{ color: palette.accent }}
        >
          {label}
        </div>
      </div>
      <h3
        className="relative mt-4 text-lg md:text-xl font-extrabold text-white leading-tight"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {title}
      </h3>
      <p className="relative mt-2 text-[13.5px] md:text-[14px] text-[#C7D0DF] leading-relaxed">
        {body}
      </p>
      {/* Mock alert */}
      <div
        className="relative mt-5 rounded-2xl p-3.5"
        style={{
          background: "rgba(4,17,31,0.55)",
          border: `1px solid ${palette.chipBorder}`,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        {alert}
      </div>
    </div>
  );
};

const ActivityCard = ({ testId, tone, icon: Icon, badge, eyebrow, children, meta }) => {
  const palette = {
    red: {
      accent: "#EF6B6B",
      bg: "linear-gradient(160deg, rgba(239,107,107,0.14) 0%, rgba(244,199,82,0.06) 100%)",
      border: "rgba(239,107,107,0.35)",
      chip: "rgba(239,107,107,0.18)",
      chipBorder: "rgba(239,107,107,0.45)",
    },
    green: {
      accent: "#22C585",
      bg: "linear-gradient(160deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.04) 100%)",
      border: "rgba(34,197,133,0.40)",
      chip: "rgba(34,197,133,0.18)",
      chipBorder: "rgba(34,197,133,0.45)",
    },
  }[tone];
  return (
    <GlassCard
      testId={testId}
      className="p-6 md:p-7"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-25 blur-3xl"
        style={{ background: palette.accent }}
      />
      <div className="relative flex items-center justify-between">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{ background: palette.chip, border: `1px solid ${palette.chipBorder}` }}
        >
          <Icon size={19} style={{ color: palette.accent }} />
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.20em]"
          style={{ background: palette.chip, border: `1px solid ${palette.chipBorder}`, color: palette.accent }}
        >
          {badge}
        </div>
      </div>
      <div
        className="relative mt-4 text-[13px] uppercase tracking-[0.20em] font-bold"
        style={{ color: palette.accent }}
      >
        {eyebrow}
      </div>
      <p className="relative mt-2 text-[15px] text-white leading-relaxed">{children}</p>
      <div className="relative mt-4 flex items-center justify-between">{meta}</div>
    </GlassCard>
  );
};

export const LiveFeedSection = () => (
  <SectionShell
    id="live-feed"
    eyebrow="Nova Live Feed"
    title="Your proactive AI brain."
    description="Nova doesn't wait for you to open the app. It reads receipts, watches return windows, and forecasts next week's basket — then pings you the moment it matters. Consider it your always-on financial bodyguard."
    testId="section-live-feed"
  >
    {/* THREE SIGNATURE DEFENSES */}
    <div className="grid gap-5 md:gap-6 lg:grid-cols-3 items-stretch">
      {/* Return Window Defense */}
      <DefenseCard
        tone="green"
        icon={RotateCcw}
        label="Return Window Defense"
        title="Never miss a return again."
        body="Nova reads every store's return policy from your scanned receipt and pushes a live countdown — so you never lose money to a missed return window."
        alert={
          <div>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.20em] font-bold text-[#22C585]">
                <ShieldCheck size={11} />
                Return alert
              </div>
              <div className="text-[9px] text-white/50 font-semibold">Best Buy</div>
            </div>
            <div
              className="mt-2 text-[13px] font-extrabold text-white leading-snug"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              You have <span className="text-[#22C585]">5 days left</span> to
              return this item.
            </div>
            <div className="mt-1 text-[11px] text-white/65">
              Bluetooth speaker · $89.99 · Store credit OK
            </div>
            {/* Digital barcode */}
            <div
              className="mt-3 rounded-lg p-2.5 flex flex-col items-center"
              style={{
                background: "rgba(255,255,255,0.94)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div className="flex items-end gap-[1.5px] h-8">
                {[3,1,2,1,3,2,1,4,2,1,3,1,2,3,1,2,4,1,3,1,2,2,3,1,4,2,1,3].map((w, i) => (
                  <div
                    key={i}
                    className="bg-[#04111F]"
                    style={{ width: `${w}px`, height: "100%" }}
                  />
                ))}
              </div>
              <div className="mt-1 text-[9px] font-mono text-[#04111F] tracking-widest">
                SN-49281-BSPKR
              </div>
            </div>
            <div className="mt-2 text-[10px] text-center text-white/70">
              Tap to show your embedded digital barcode to the cashier.
            </div>
          </div>
        }
        testId="defense-return-window"
      />

      {/* Coupon & Reward Expiry Tracking */}
      <DefenseCard
        tone="amber"
        icon={TicketPercent}
        label="Coupon & Reward Guard"
        title="Free money, never wasted."
        body="The Mindee-powered scanner automatically isolates the reward slips printed on your paper receipts and drops them into your Live Feed 48 hours before they expire — so you never leave savings on the table."
        alert={
          <div>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.20em] font-bold text-[#F4C752]">
                <Timer size={11} />
                Reward expires soon
              </div>
              <div className="text-[9px] text-white/50 font-semibold">Kroger</div>
            </div>
            <div
              className="mt-2 text-[13px] font-extrabold text-white leading-snug"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              Save <span className="text-[#F4C752]">$5</span> on your next
              grocery run.
            </div>
            <div className="mt-1 text-[11px] text-white/65">
              Auto-isolated from scanned receipt #KR-7743
            </div>
            {/* Countdown bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-white/60">Expires in</span>
                <span className="text-[#F4C752] font-bold">48 h : 12 m</span>
              </div>
              <div
                className="mt-1 w-full h-1.5 rounded-full overflow-hidden"
                style={{ background: "rgba(255,255,255,0.10)" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: "22%",
                    background: "linear-gradient(90deg, #F4C752 0%, #F5A742 100%)",
                  }}
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[10px] text-white/70">
              <ScanLine size={11} className="text-[#F4C752]" />
              Isolated automatically from paper receipt.
            </div>
          </div>
        }
        testId="defense-coupon-guard"
      />

      {/* Predictive Budgeting */}
      <DefenseCard
        tone="purple"
        icon={TrendingUp}
        label="Predictive Basket"
        title="Know the bill before you shop."
        body="Nova learns your habits from every scanned basket and forecasts next week's grocery run to the dollar — with a transparent ±10% volatility buffer — before you ever step foot in the store."
        alert={
          <div>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.20em] font-bold text-[#B7A5FF]">
                <LineChart size={11} />
                Weekly forecast
              </div>
              <div className="text-[9px] text-white/50 font-semibold">Sun · Nov 24</div>
            </div>
            <div
              className="mt-2 flex items-baseline gap-1.5"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              <span className="text-2xl font-extrabold text-white">$87.20</span>
              <span className="text-[11px] text-[#B7A5FF] font-bold">± 10%</span>
            </div>
            <div className="mt-1 text-[11px] text-white/65">
              Confidence range · $78.48 – $95.92
            </div>
            {/* Tiny sparkline */}
            <svg
              viewBox="0 0 200 60"
              className="mt-3 w-full h-10"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="skFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B7A5FF" stopOpacity="0.40" />
                  <stop offset="100%" stopColor="#B7A5FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,45 L25,38 L50,42 L75,30 L100,34 L125,22 L150,26 L175,18 L200,20 L200,60 L0,60 Z"
                fill="url(#skFill)"
              />
              <path
                d="M0,45 L25,38 L50,42 L75,30 L100,34 L125,22 L150,26 L175,18 L200,20"
                stroke="#B7A5FF"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-2 text-[10px] text-white/70">
              Based on 12 weeks of scanned baskets.
            </div>
          </div>
        }
        testId="defense-predictive-basket"
      />
    </div>

    {/* LIVE EXAMPLES */}
    <div className="mt-12 md:mt-14">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">
          Live from your feed
        </div>
        <h3
          className="mt-2 text-xl md:text-2xl font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          Little wins, in real time.
        </h3>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-2 max-w-4xl mx-auto">
        <ActivityCard
          testId="live-feed-card-a"
          tone="red"
          icon={Gift}
          badge="Holiday nudge"
          eyebrow="155 days until Christmas"
          meta={
            <>
              <div className="text-[11px] text-white/60">Auto-stash suggestion</div>
              <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#22C585]">
                Set a rule <ArrowRight size={13} />
              </div>
            </>
          }
        >
          Save an extra{" "}
          <span className="font-extrabold text-[#F4C752]">$10 a week</span> now
          to buy that perfect gift for your special someone or the kids.
        </ActivityCard>

        <ActivityCard
          testId="live-feed-card-b"
          tone="green"
          icon={Trophy}
          badge="Savings win"
          eyebrow="+ $200 this month"
          meta={
            <>
              <div className="text-[11px] text-white/60">Streak · 3 months</div>
              <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-white">
                View trend <ArrowRight size={13} />
              </div>
            </>
          }
        >
          Awesome job,{" "}
          <span className="font-extrabold text-[#22C585]">Topaz!</span> You
          saved an extra{" "}
          <span className="font-extrabold text-[#22C585]">$200</span> this
          month. Keep up the great momentum!
        </ActivityCard>
      </div>
    </div>
  </SectionShell>
);
